import BmmpAsset from "./bmmp-asset";
import Log from "app/utils/log";
import ErrorReport from "app/utils/error-report";
import LoadTimer from "app/utils/load-timer";
import { getServerUrl } from "../../utils/serverUrlUtils";
import { BLP_REGION_ERROR } from "app/utils/Constant";

const log = new Log("BmmrLoader");
// allowed distributions for using the blp service env
const ALLOWED_BLP_DISTRIBUTIONS = new Set(["SEMR_WEB", "SEMR_TERMINAL", "ETKB_WEB"]);

/**
 * Ajax wrapper that retrieves data from BMMR
 * @class Asset
 */
class BmmrLoader {
    private readonly htmlChildId: any;
    private readonly url: string;
    private readonly _assetDetails: string;
    private readonly isLive: boolean;
    private _options: Record<string, string>;
    private _loadDataPromise: Promise<any>;

    /**
     * Load stream data
     * @returns {Promise.<BmmpAsset|undefined>} Stream data
     */
    load() {
        if (this.isLive) {
            return Promise.resolve();
        }

        if (this._options.url_override) {
            return Promise.resolve();
        }

        if (this._loadDataPromise) {
            return this._loadDataPromise;
        }

        this._loadDataPromise = this._assetDetails ?
            Promise.resolve(createAsset(this._assetDetails)) :
            this.loadBmmrMetadata();

        return this._loadDataPromise;
    }

    /**
     * @constructs BmmrLoader
     * @param {object} [options] user options
     * @param {object} [assetDetails] asset details
     */
    constructor(options: Record<string, string> = {}, assetDetails) {
        const { serviceEnv, _devServerUrl, id, idType, htmlChildId, accessKey, distributions = "" } = options;

        this.isLive = Boolean(options.live);

        // block to protect blp service env from serving non-news videos
        if ("blpprod" === serviceEnv) {
            // split differently depending on how distribution string is split by a comma or an uri encoded comma special character
            const requestedDistributions = distributions.includes(",") ? distributions.split(",") : distributions.split(encodeURIComponent(","));
            // blp env videos must include distributions as part of the request
            if (requestedDistributions.length <= 0) {
                throw new Error(BLP_REGION_ERROR);
            }
            requestedDistributions.forEach((distribution) => {
                if (!ALLOWED_BLP_DISTRIBUTIONS.has(distribution)) {
                    throw new Error(BLP_REGION_ERROR);
                }
            });
        }

        if (this.isLive) {
            return;
        }

        if (!id) {
            throw new Error("BmmrLoader Missing required option. Please check that id is supplied.");
        }

        this.url = `${ getServerUrl(serviceEnv, _devServerUrl) }?id=${ id }`;
        this.htmlChildId = htmlChildId;
        this._assetDetails = assetDetails;
        this._options = options;

        if (this.url.startsWith("//")) {
            this.url = "https:" + this.url;
        }

        if (idType) {
            this.url += `&idType=${ idType }`;
        }

        if (accessKey) {
            this.url += `&accessKey=${ accessKey }`;
        }

        if (distributions) {
            this.url += `&distributions=${ distributions }`;
        }
    }

    /**
     * Fetches BMMR data
     * @returns {Promise.<BmmpAsset>}
     */
    loadBmmrMetadata() {
        if (this.isLive) {
            return Promise.reject(new Error("BmmrLoader Live video flag is set"));
        }

        const config = {
            mode: "cors",
            credentials: "same-origin",
            cache: "no-cache"
        };

        LoadTimer.trackLoadStart(this.htmlChildId, LoadTimer.loadTypes.BMMR, this.url);

        return fetch(this.url, config as RequestInit)
            .then(this.handleBmmrResponse.bind(this))
            .then((rawBmmr) => {
                const asset = createAsset(rawBmmr);
                LoadTimer.trackLoadComplete(this.htmlChildId, LoadTimer.loadTypes.BMMR, LoadTimer.loadStatus.COMPLETE);
                return asset;
            })
            .catch((error) => {
                ErrorReport.recordLoadError(log, this.url, error);
                LoadTimer.trackLoadComplete(this.htmlChildId, LoadTimer.loadTypes.BMMR, LoadTimer.loadStatus.ERROR);
            });
    }

    /**
     * Handle the response from BMMR
     * @param {Response} response response object from Fetch
     * @returns {object} Response from BMMR parsed as JSON
     * @throws {Error} Expects the response status to be 200
     */
    handleBmmrResponse(response) {
        if (response.status !== 200) {
            throw new Error(
                `BmmrLoader Unable to load BMMR metadata for ${ this.url } with status ${ response.status }`);
        }
        return response.json();
    }
}

function createAsset(response) {
    return new BmmpAsset(response);
}

export default BmmrLoader;
